// CHANGE-042: a company's or partner's logo, and the one control to change it (Profile pages and staff pages).
import { useRef, useState } from 'react'
import { api } from '../lib'
import { ErrorBox } from './ui'

const MAX_BYTES = 2 * 1024 * 1024

/** The uploaded logo, else the name's first letter. */
export function LogoTile({ name, url, size = 'sm', testid }: { name: string; url?: string | null; size?: 'sm' | 'lg'; testid?: string }) {
  if (url) return <img className={`logo-mark logo-${size}`} src={url} alt="" data-testid={testid} />
  return <span className={`logo-mark logo-${size} letter`} aria-hidden="true" data-testid={testid}>{name.trim().charAt(0).toUpperCase() || '·'}</span>
}

/** The logo with "Upload logo" and "Remove". Each saves at once; `path` takes PUT (the file) and DELETE. */
export function LogoEdit({ name, url, path, canEdit, onChange, testid }: {
  name: string; url?: string | null; path: string; canEdit: boolean; onChange: () => void; testid: string
}) {
  const input = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true)
    setError(null)
    try {
      await fn()
      onChange()
    } catch (e) {
      setError(e)
    } finally {
      setBusy(false)
    }
  }
  const pick = (file: File | undefined) => {
    if (input.current) input.current.value = ''
    if (!file) return
    if (file.size > MAX_BYTES) {
      setError(new Error('The logo must be 2 MB or less.'))
      return
    }
    run(() => api('PUT', path, file))
  }
  return (
    <div className="logo-edit" data-testid={testid}>
      <LogoTile name={name} url={url} size="lg" testid={`${testid}-image`} />
      {canEdit && (
        <div className="row wrap">
          <input ref={input} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" hidden
            data-testid={`${testid}-file`} onChange={(e) => pick(e.target.files?.[0])} />
          <button type="button" disabled={busy} onClick={() => input.current?.click()} data-testid={`${testid}-upload`}>
            Upload logo
          </button>
          {url && (
            <button type="button" disabled={busy} onClick={() => run(() => api('DELETE', path))} data-testid={`${testid}-remove`}>
              Remove
            </button>
          )}
        </div>
      )}
      {!!error && <ErrorBox error={error} />}
    </div>
  )
}
